import { ExecutionJobService, type RunTarget } from '../../../services/execution-job.service';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerTargetSelectComponent } from '../../../components/runner-target-select/runner-target-select.component';

export type RunMode = 'MANUAL' | 'LOCAL_AUTOMATED';

/** In der Reihenfolge der Knoepfe im Template. */
const RUN_MODES: readonly RunMode[] = ['MANUAL', 'LOCAL_AUTOMATED'];

const ARROW_STEP: Readonly<Record<string, number>> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

export interface RunTestCaseDialogData {
  projectId: string;
  testCaseId: string;
  testCaseKey: string | null;
  testCaseTitle: string;
}

export interface RunTestCaseDialogResult {
  mode: RunMode;
  /**
   * Rechner oder Anforderung; `null` nur bei MANUAL. Automatisiert startet der Dialog erst mit
   * gewaehltem Ziel. Ein Rechner traegt seinen Namen mit, damit eine Absage ihn nennen kann —
   * die Liste gibt es nur hier im Dialog.
   */
  target: RunTarget | null;
}

/**
 * Auswahl, wie ein Testfall ausgefuehrt wird.
 *
 * Entspricht dem Classic-Vorgehen: der Tester waehlt beim Start, ob er selbst
 * durchklickt oder eine Engine den Lauf uebernimmt. Bei der automatisierten
 * Variante waehlt er zusaetzlich den Rechner — der Auftrag wird eingereiht und
 * von genau diesem abgeholt.
 *
 * <p><b>Ohne Design-Entwurf fuer die Rechner-Auswahl.</b> Das Bundle kennt die Zeile "Laeuft auf"
 * nicht — es entstand, bevor Auftraege einen Empfaenger trugen. Umgesetzt im Stil des umgebenden
 * Dialogs, ohne neue Farben oder Formen. Das gilt auch fuer den Online-Zustand in der Auswahl
 * (offline Rechner stehen ausgegraut und nicht waehlbar darin), die Ladezeile und die
 * Fehlermeldung mit "Erneut versuchen" und die Pflichtauswahl mit dem Platzhalter "Rechner
 * waehlen" (NEX-1513; der Entwurf im Ticket zeigt noch den alten Weg mit Token) und die Gruppen
 * "Rechner" und "Anforderung" mit den Eintraegen "Irgendeiner mit …" (NEX-1257). Das ist eine
 * Luecke, keine Entscheidung: Liegt ein Entwurf vor, gehoert das hier angeglichen (NEX-1093).
 * CLAUDE.md Regel 9 verlangt bei UI-Arbeit einen Verweis aufs Bundle; dieser Absatz steht an seiner Stelle, damit die fehlende Vorlage
 * nicht unbemerkt zur Vorlage wird.
 */
@Component({
  selector: 'app-run-test-case-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    RunnerTargetSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './run-test-case-dialog.component.html',
  styleUrl: './run-test-case-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunTestCaseDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<RunTestCaseDialogComponent, RunTestCaseDialogResult>);
  readonly data = inject<RunTestCaseDialogData>(MAT_DIALOG_DATA);

  readonly selected = signal<RunMode>('MANUAL');

  /**
   * Ausfuehrbare Schritte des gespeicherten Testfalls laut Backend, Module aufgeloest (NEX-1504).
   * `null`, solange die Antwort aussteht. Ungespeicherte Aenderungen zaehlen bewusst nicht: Laufen
   * wuerde ohnehin der gespeicherte Stand.
   */
  readonly stepCount = signal<number | null>(null);
  readonly stepCountFailed = signal(false);

  /** Laden, Fehler und 0 sperren gleich: Automatisiert startet nur, was sicher Schritte hat. */
  readonly localAvailable = computed(() => (this.stepCount() ?? 0) > 0);

  private userChose = false;

  /** Rechner oder Anforderung aus der Auswahl; `null`, solange keins feststeht. */
  readonly target = signal<RunTarget | null>(null);

  readonly selectedRunnerId = computed(() => {
    const target = this.target();
    return target?.kind === 'runner' ? target.runnerId : null;
  });

  /** Gestartet wird automatisiert erst, wenn feststeht, auf welchem Rechner. */
  readonly startDisabled = computed(() => this.selected() === 'LOCAL_AUTOMATED' && this.target() === null);

  constructor() {
    if (!this.data.testCaseId) {
      this.stepCount.set(0);
      return;
    }
    inject(ExecutionJobService)
      .executableStepCount(this.data.projectId, this.data.testCaseId)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (count) => {
          this.stepCount.set(count);
          if (count > 0 && !this.userChose) {
            this.selected.set('LOCAL_AUTOMATED');
          }
        },
        error: () => this.stepCountFailed.set(true),
      });
  }

  select(mode: RunMode): void {
    this.userChose = true;
    this.selected.set(mode);
  }

  private readonly optionButtons = viewChildren<ElementRef<HTMLButtonElement>>('runOption');

  /**
   * Pfeiltasten wie in einer nativen Radiogruppe: Sie waehlen und fokussieren die naechste
   * Option, am Ende geht es vorne weiter. Gesperrte Optionen werden uebersprungen.
   */
  onOptionKeydown(event: KeyboardEvent): void {
    const step = ARROW_STEP[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const enabled = RUN_MODES.filter((mode) => this.isSelectable(mode));
    const next = enabled[(enabled.indexOf(this.selected()) + step + enabled.length) % enabled.length];
    this.select(next);
    this.optionButtons()[RUN_MODES.indexOf(next)]?.nativeElement.focus();
  }

  private isSelectable(mode: RunMode): boolean {
    return mode === 'MANUAL' || this.localAvailable();
  }

  start(): void {
    const mode = this.selected();
    this.dialogRef.close({ mode, target: mode === 'MANUAL' ? null : this.target() });
  }

  /** Auch beim Sprung auf die Kontoseite: Sonst laege der Dialog ueber der Zielseite. */
  cancel(): void {
    this.dialogRef.close();
  }
}
